import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { GraduationCap, Check, Clock } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
  "Six in-depth training modules",
  "Vocabulary through sentence-building and assessment",
  "50+ ready-to-use printable templates and answer keys",
  "SPED, ESL/ELL, and OT modification guidance",
  "AI Tutor · PDF Vault · Completion Certificate",
  "Lifetime access with no subscription or renewals",
];

export const CourseComingSoon = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState(false);

  const join = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Please enter your email address");
    setBusy(true);
    try {
      const res = await axios.post(`${API}/course/waitlist`, { email });
      setJoined(true);
      toast.success(res.data.status === "already_on_waitlist" ? "You're already on the waitlist!" : "You're on the waitlist! 🎉");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="course" className="py-10 sm:py-14 px-4 scroll-mt-24" aria-labelledby="course-title">
      <div className="max-w-6xl mx-auto">
        <div className="relative bg-[#0f285f] text-white border-0 rounded-3xl overflow-hidden" data-testid="course-coming-soon">
          <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:24px_24px]" />
          <div className="relative grid lg:grid-cols-5 gap-8 p-7 sm:p-12">
            <div className="lg:col-span-3">
              <span className="inline-flex items-center gap-2 bg-[#ff8518] text-white border-0 rounded-full px-3 py-1.5 mb-4 font-nunito font-black text-xs uppercase tracking-widest" data-testid="course-coming-soon-badge">
                <Clock size={14} weight="bold" /> Coming Soon
              </span>
              <h2 id="course-title" className="font-nunito font-extrabold tracking-tight text-3xl sm:text-4xl text-white leading-tight mb-3">
                A Comprehensive Vocabulary and Language Support System
              </h2>
              <p className="font-nunito text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl mb-6">
                A step-by-step framework for helping diverse learners build vocabulary, strengthen sentences, and communicate with confidence.
              </p>
              <ul className="grid sm:grid-cols-2 gap-2.5">
                {FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 font-nunito text-sm sm:text-base text-slate-200">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0"><Check size={12} weight="bold" /></span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-2">
              <div className="bg-white text-[#17284b] border border-[#d4dff0] rounded-2xl p-6">
                <div className="w-12 h-12 bg-yellow-200 border border-[#d4dff0] rounded-2xl flex items-center justify-center mb-4">
                  <GraduationCap size={26} weight="duotone" />
                </div>
                {joined ? (
                  <div data-testid="waitlist-success">
                    <h3 className="font-nunito font-extrabold tracking-tight text-xl mb-2">You're on the list! 🎉</h3>
                    <p className="font-nunito text-sm text-[#596882]">We'll email you the moment the course opens — no spam, ever.</p>
                  </div>
                ) : (
                  <form onSubmit={join} className="space-y-3">
                    <h3 className="font-nunito font-extrabold tracking-tight text-xl">Be the first to know</h3>
                    <p className="font-nunito text-sm text-[#596882]">Join the waitlist and we'll let you know when enrollment opens.</p>
                    <Input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your email address"
                      className="font-nunito border border-[#d4dff0] rounded-xl py-5"
                      data-testid="waitlist-email-input"
                      aria-label="Email for course waitlist"
                    />
                    <Button type="submit" disabled={busy} className="w-full bg-[#ff8518] hover:bg-[#e8740f] text-white font-nunito font-bold border border-[#d4dff0] rounded-xl py-5" data-testid="waitlist-submit-btn">
                      {busy ? "Joining…" : "Coming Soon — Join the Waitlist"}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
